import React, { useState, useEffect, useRef, useCallback } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Accounting from './pages/Accounting';
import Income from './pages/Income';
import Deposit from './pages/Deposit';
import Budget from './pages/Budget';
import PrepareCredit from './pages/PrepareCredit';
import Contract from './pages/Contract';
import Zemanat from './pages/Zemanat';
import Treasury from './pages/Treasury';
import Tadarokat from './pages/Tadarokat';
import InOut from './pages/InOut';
import Evaluation from './pages/Evaluation';
import Property from './pages/Property';
import AdminSystem from './pages/Admin/AdminSystem';
import { TabProvider } from './pages/TabManager';
import ChatWidget from './chat/ChatWidget';

function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp > Math.floor(Date.now() / 1000);
  } catch { return false; }
}

// ── قفل برای جلوگیری از refresh همزمان ──
let refreshPromise = null;

async function tryRefresh() {
  // اگه یه refresh در حال اجراست، منتظر همون باش
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refresh = localStorage.getItem('refresh');
    if (!refresh || !isTokenValid(refresh)) return false;
    try {
      const res = await fetch('/api/auth/token/refresh/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      localStorage.setItem('access', data.access);
      if (data.refresh) localStorage.setItem('refresh', data.refresh);
      return true;
    } catch { return false; }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// ── نگهبان سراسری: هر پاسخ ۴۰۱ روی API را می‌بیند و اگر توکن refresh هم
//    دیگر معتبر نباشد، بی‌درنگ کل نشست را می‌بندد (به‌جای انتظار برای بررسی دوره‌ای).
//    یک‌بار روی window.fetch نصب می‌شود و همه‌ی صفحات را پوشش می‌دهد.
if (typeof window !== 'undefined' && !window.__arjanAuthGuard) {
  window.__arjanAuthGuard = true;
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await _origFetch(...args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (res.status === 401 && url.includes('/api/') && !url.includes('/api/auth/')) {
        const refresh = localStorage.getItem('refresh');
        if (!refresh || !isTokenValid(refresh)) {
          window.dispatchEvent(new Event('arjan:auth-expired'));
        }
      }
    } catch { /* نادیده */ }
    return res;
  };
}

export default function App() {
  const [user, setUser]     = useState(null);
  const [page, setPage]     = useState('dashboard');
  const [ready, setReady]   = useState(false);

  // ── افتا: پیام ورود شخص دیگر ──
  const [displaced, setDisplaced] = useState(null);  // {ip, msg}
  const sessionRef = useRef('');  // session_token فعلی

  // ── لاگ‌اوت فوری وقتی توکن نامعتبر است (از نگهبان سراسری fetch) ──
  useEffect(() => {
    const onExpired = () => {
      localStorage.clear();
      setUser(null);
      setDisplaced(null);
    };
    window.addEventListener('arjan:auth-expired', onExpired);
    return () => window.removeEventListener('arjan:auth-expired', onExpired);
  }, []);

  useEffect(() => {
    async function check() {
      const saved = localStorage.getItem('user');
      const access = localStorage.getItem('access');
      if (!saved) { setReady(true); return; }
      if (isTokenValid(access)) {
        setUser(JSON.parse(saved));
        sessionRef.current = localStorage.getItem('session_token') || '';
      } else {
        const ok = await tryRefresh();
        if (ok) {
          setUser(JSON.parse(saved));
          sessionRef.current = localStorage.getItem('session_token') || '';
        }
        else localStorage.clear();
      }
      setReady(true);
    }
    check();
  }, []);

  // ── بررسی دوره‌ای access token — هر ۲ دقیقه ──
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      const access = localStorage.getItem('access');
      if (isTokenValid(access)) return;
      const ok = await tryRefresh();
      if (!ok) {
        localStorage.clear();
        setUser(null);
        alert('نشست منقضی شد. لطفاً دوباره وارد شوید.');
      }
    }, 2 * 60 * 1000);  // هر ۲ دقیقه
    return () => clearInterval(interval);
  }, [user]);

  // ── افتا: بررسی session_token هر ۳۰ ثانیه ──
  useEffect(() => {
    if (!user) return;
    const checkSession = async () => {
      const access = localStorage.getItem('access');
      if (!access || !isTokenValid(access)) return;
      const t = sessionRef.current;
      try {
        const res = await fetch(`/api/auth/session-check/?t=${encodeURIComponent(t)}`, {
          headers: { Authorization: `Bearer ${access}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.ok && !data.valid) {
          // شخص دیگری وارد شده
          setDisplaced({ ip: data.new_ip, msg: data.msg });
        }
      } catch { /* شبکه قطع — نادیده بگیر */ }
    };

    const interval = setInterval(checkSession, 30 * 1000);
    // اولین بار هم چک کن
    checkSession();
    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = useCallback((u, sessionToken) => {
    sessionRef.current = sessionToken || '';
    localStorage.setItem('session_token', sessionToken || '');
    setUser(u);
    setPage('dashboard');
    setDisplaced(null);
  }, []);

  if (!ready) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
      minHeight:'100vh',fontFamily:'Tahoma',fontSize:'16px',color:'#555'}}>
      در حال بارگذاری...
    </div>
  );

  if (!user) return <Login onLogin={handleLogin} />;

  const logout = () => { localStorage.clear(); setUser(null); };

  // ── پاپ‌آپ افتا: ورود شخص دیگر ──
  const displacedPopup = displaced && (
    <div style={ds.overlay}>
      <div style={ds.modal}>
        <div style={ds.icon}>⚠️</div>
        <div style={ds.title}>نشست ابطال شد</div>
        <div style={ds.msg}>{displaced.msg}</div>
        <div style={ds.sub}>
          برای امنیت حساب شما، این نشست پایان یافت.
          <br/>اگر این ورود توسط شما نبوده، رمز عبور خود را تغییر دهید.
        </div>
        <button onClick={() => { localStorage.clear(); setUser(null); setDisplaced(null); }} style={ds.btn}>
          ورود مجدد
        </button>
      </div>
    </div>
  );

  /* ── انتخاب صفحه فعال ── */
  const renderPage = () => {
    switch (page) {
      case 'accounting':
        return <Accounting user={user} onBack={() => setPage('dashboard')} />;
      case 'income':
        return <Income user={user} onBack={() => setPage('dashboard')} />;
      case 'deposit':
        return <Deposit user={user} onBack={() => setPage('dashboard')} />;
      case 'budget':
        return <Budget user={user} onBack={() => setPage('dashboard')} />;
      case 'prepare_credit':
        return <PrepareCredit user={user} onBack={() => setPage('dashboard')} />;
      case 'contract':
        return <Contract user={user} onBack={() => setPage('dashboard')} />;
      case 'zemanat':
        return <Zemanat user={user} onBack={() => setPage('dashboard')} />;
      case 'treasury':
        return <Treasury user={user} onBack={() => setPage('dashboard')} />;
      case 'tadarokat':
        return <Tadarokat user={user} onBack={() => setPage('dashboard')} />;
      case 'inout':
        return <InOut user={user} onBack={() => setPage('dashboard')} />;
      case 'evaluation':
        return <Evaluation user={user} onBack={() => setPage('dashboard')} />;
      case 'property':
        return <Property user={user} onBack={() => setPage('dashboard')} />;
      case 'system':
        return <AdminSystem user={user} onBack={() => setPage('dashboard')} />;
      default:
        return <Dashboard user={user} onLogout={logout} onNavigate={setPage} />;
    }
  };

  return (
    <TabProvider globalOverlay={<ChatWidget page={page} />}>
      {renderPage()}
      {displacedPopup}
    </TabProvider>
  );
}

// ── استایل پاپ‌آپ ورود شخص دیگر ──
const ds = {
  overlay: {
    position:'fixed', inset:0, background:'rgba(10,22,40,0.85)',
    display:'flex', alignItems:'center', justifyContent:'center',
    zIndex:99999, padding:'20px',
  },
  modal: {
    background:'#fff', borderRadius:'16px', padding:'34px 30px 28px',
    width:'100%', maxWidth:'400px', textAlign:'center', direction:'rtl',
    boxShadow:'0 30px 80px rgba(0,0,0,0.5)',
  },
  icon:  { fontSize:'50px', marginBottom:'12px' },
  title: { fontSize:'18px', fontWeight:'700', color:'#c62828', marginBottom:'10px' },
  msg:   { fontSize:'14px', color:'#333', lineHeight:'1.9', marginBottom:'8px',
           background:'#fff3e0', borderRadius:'8px', padding:'12px', border:'1px solid #ffb74d' },
  sub:   { fontSize:'12px', color:'#777', lineHeight:'1.8', marginBottom:'18px' },
  btn:   {
    padding:'12px 0', width:'100%',
    background:'linear-gradient(135deg,#c62828,#b71c1c)', color:'#fff',
    border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700',
    fontFamily:'inherit', cursor:'pointer',
  },
};